"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";

/*
  ─── FAQ SEO NOTES ──────────────────────────────────────────────────────────
  Questions are derived from:
  1. Live Google PAA/Autocomplete research (April 2026)
  2. Your keyword planner bands
  3. Real user pain points from CompressIMG, MetaClean, ImgShare, iFormat blogs

  Each question + answer naturally embeds keyword targets:
  - "compress image without losing quality" (100K–1M)
  - "remove metadata from jpg / erase exif data" (1K–10K)
  - "how to safely share photos online" (1K–10K)
  - "convert png to jpg free / change image format" (10K–100K)
  - "pdf tools online / compress pdf online" (10K–100K)
  - "resize image for social media / instagram photo resizer" (10K–100K)
  - "pagespeed image optimizer / optimize images for core web vitals" (1K–10K)
  - "strong password generator online" (10K–100K)
  - "check image resolution online / online image dpi checker" (1K–10K)
  - "favicon generator for website" (10K–100K)

  The schema in contact/page.tsx must mirror these Q&A pairs exactly.
  ─────────────────────────────────────────────────────────────────────────
*/

const FAQ = [
  {
    question: "Does compressing an image actually reduce visible quality?",
    answer: [
      "Not noticeably if done correctly. JPEG images at quality 80–85% usually look the same on screen and become much smaller.",
      "For logos and sharp graphics, use PNG or lossless WebP. Snappy Fix helps compress image files without visible artifacts.",
    ],
  },
  {
    question:
      "Does Instagram or WhatsApp automatically remove GPS data from my photos?",
    answer: [
      "It depends on how you share. Instagram strips GPS metadata from public posts and Stories.",
      "WhatsApp can keep metadata when you send a photo as a document. Strip EXIF data before uploading to any platform.",
      "Snappy Fix removes GPS, device model, timestamps, and hidden EXIF fields with no sign-up.",
    ],
  },
  {
    question: "What is the best image format for websites in 2026?",
    answer: [
      "WebP is the practical standard for most websites. It is smaller than JPEG at similar quality and works in modern browsers.",
      "AVIF can be even smaller, but it may encode more slowly. For logos, icons, and text graphics, use lossless WebP or PNG.",
    ],
  },
  {
    question: "How do I reduce a PDF file size without losing quality?",
    answer: [
      "The most effective method is compressing the images inside the PDF. Images often make up most of the file size.",
      "Snappy Fix re-encodes those images at an optimised setting. This can shrink PDFs with no visible loss on screen or in print.",
    ],
  },
  {
    question: "Why do my images look blurry after uploading to Instagram?",
    answer: [
      "Instagram recompresses uploads. Oversized images also get resized, which can reduce sharpness.",
      "Export at the correct dimensions before uploading. Snappy Fix creates the precise social media image sizes each platform needs.",
    ],
  },
  {
    question:
      "How do I resize a photo to exactly 3.5 cm × 4.5 cm or 200×230 pixels for a government form?",
    answer: [
      "Government portals often require exact passport photo sizes, such as 35mm×45mm or 200×230 pixels.",
      "Use the image resizer to enter the required dimensions. Then use the compressor to meet the file size limit, such as 100 KB.",
    ],
  },
  {
    question:
      "How do I check if my image is 300 DPI before sending it to print?",
    answer: [
      "DPI affects print sharpness. 300 DPI is a common standard for professional print.",
      "Snappy Fix's DPI checker reads the embedded resolution and shows whether the image meets print requirements.",
    ],
  },
  {
    question: "Are all Snappy Fix tools genuinely free with no sign-up?",
    answer: [
      "Yes. Every Snappy Fix tool is free for standard use, with no account and no watermarks.",
      "This includes image conversion, PDF compression, EXIF removal, favicon generation, and the password generator.",
    ],
  },
  {
    question: "What size should a favicon be, and which formats does it need?",
    answer: [
      "A complete favicon set usually includes 16×16, 32×32, and 48×48 ICO files.",
      "It should also include 180×180 PNG for Apple Touch icons and 192×192 PNG for Android. Snappy Fix generates these from one PNG or JPG.",
    ],
  },
  {
    question: "What makes a strong password in 2026?",
    answer: [
      "Use at least 16 characters with uppercase letters, lowercase letters, numbers, and symbols.",
      "Avoid names, dictionary words, and keyboard patterns. Snappy Fix can generate random passwords and check password strength.",
    ],
  },
];

export function ContactFaq() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return (
    <section className="py-16 bg-gradient-to-br from-[#faf7ff] to-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center space-y-3 mb-10">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#c3003a] bg-[#fb397d]/10 px-4 py-1.5 rounded-full">
            FAQ
          </span>
          {/*
            H2 targets:
            "compress image without losing quality", "remove metadata from jpg",
            "free online image tools", "pdf tools online"
          */}
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#2b1d3a]">
            Questions About Snappy Fix Tools
          </h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto">
            Common questions about image compression, PDF processing, metadata
            removal, social media resizing, and how our free tools work.
          </p>
        </div>

        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div
              key={i}
              className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                openFaq === i
                  ? "border-[#5b32b4]/40 bg-white shadow-md shadow-[#5b32b4]/10"
                  : "border-[#e9e1ff] bg-white hover:border-[#d4c5f9]"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                aria-expanded={openFaq === i}
                aria-controls={`faq-answer-${i}`}
              >
                <span className="font-bold text-[#2b1d3a] text-sm sm:text-base">
                  {item.question}
                </span>
                <ChevronDown
                  size={18}
                  className={`text-[#5b32b4] shrink-0 transition-transform duration-300 ${
                    openFaq === i ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                id={`faq-answer-${i}`}
                role="region"
                className={`grid transition-all duration-300 ${
                  openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-5 space-y-3">
                    {item.answer.map((paragraph) => (
                      <p
                        key={paragraph}
                        className="text-gray-500 text-sm leading-relaxed"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
